'use client';

import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import {
  Building2,
  BarChart3,
  Globe,
  Users,
  CreditCard,
  Shield,
  CheckCircle2,
  ArrowRight,
  Smartphone,
  Star,
  Search,
  Zap,
} from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { PricingSection } from './PricingSection';
import { Testimonials } from './Testimonials';
import { TrustBadgesSection } from './TrustBadgesSection';
import { FAQSection } from './FAQSection';
import { usePageContent } from '@/core/i18n/content';
import { HOME_CONTENT, type HomeContent } from '@/core/i18n/content/home.content';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Icon map — matches feature items to their icons by index                   */
/* -------------------------------------------------------------------------- */

const FEATURE_ICONS: LucideIcon[] = [Building2, BarChart3, Globe, Users, CreditCard, Shield];
const PORTAL_ICONS: LucideIcon[] = [Search, Smartphone, Star, Zap];

/* -------------------------------------------------------------------------- */
/*  Section 1 — Hero                                                         */
/* -------------------------------------------------------------------------- */

function HeroSection({ c }: { c: HomeContent }) {
  const { t } = useTranslation();
  return (
    <section id="for-hotels-hero" className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 80%, rgba(13,158,153,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <h1
          id="for-hotels-hero-title"
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {c.hero.title}
        </h1>
        <p id="for-hotels-hero-subtitle" className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {c.hero.subtitle}
        </p>
        <div id="for-hotels-hero-actions" className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id="for-hotels-hero-get-started-btn"
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg',
              'shadow-lg shadow-hms-gold/20 transition-all duration-200',
              'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {c.hero.ctaStart}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id="for-hotels-hero-demo-btn"
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {c.hero.ctaDemo}
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 2 — Platform Features                                            */
/* -------------------------------------------------------------------------- */

function PlatformFeaturesSection({ c }: { c: HomeContent }) {
  return (
    <section id="for-hotels-features" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            id="for-hotels-features-title"
            className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}
          >
            {c.features.title}
          </h2>
          <p id="for-hotels-features-subtitle" className="mt-4 text-base text-hms-textSub sm:text-lg">
            {c.features.subtitle}
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {c.features.items.map((item, idx) => {
            const Icon = FEATURE_ICONS[idx] ?? Building2;
            return (
              <div
                key={idx}
                id={`for-hotels-feature-card-${idx}`}
                className={cn('group relative rounded-xl border border-hms-border bg-hms-card p-6', 'transition-all duration-300', 'hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20')}
              >
                <div className={cn('mb-4 flex h-12 w-12 items-center justify-center rounded-full', 'bg-hms-gold/10 transition-colors duration-300 group-hover:bg-hms-gold/15')}>
                  <Icon className="h-5 w-5 text-hms-gold" />
                </div>
                <h3 id={`for-hotels-feature-title-${idx}`} className="mb-2 text-base font-bold text-hms-text tracking-tight">{item.title}</h3>
                <p className="text-sm leading-relaxed text-hms-textSub">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 3 — Why Choose HotelStack                                        */
/* -------------------------------------------------------------------------- */

function WhyChooseSection({ c }: { c: HomeContent }) {
  return (
    <section id="for-hotels-why-choose" className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 30% 50%, rgba(200,145,58,0.05) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            id="for-hotels-why-title"
            className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}
          >
            {c.whyChoose.title}
          </h2>
          <p id="for-hotels-why-subtitle" className="mt-4 text-base text-hms-textSub sm:text-lg">
            {c.whyChoose.subtitle}
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
          <div id="for-hotels-stats-grid" className="grid grid-cols-2 gap-4">
            {c.stats.map((stat, idx) => (
              <div
                key={idx}
                id={`for-hotels-stat-${idx}`}
                className={cn('flex flex-col items-center justify-center rounded-xl border border-hms-border bg-hms-card p-6', 'text-center')}
              >
                <span className={cn('text-3xl font-extrabold tracking-tight', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}>
                  {stat.value}
                </span>
                <span className="mt-1 text-sm font-medium text-hms-textSub">{stat.label}</span>
              </div>
            ))}
          </div>
          <div id="for-hotels-benefits-list" className="space-y-4">
            {c.benefits.map((benefit, idx) => (
              <div key={idx} id={`for-hotels-benefit-${idx}`} className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hms-green/10">
                  <CheckCircle2 className="h-4 w-4 text-hms-green" />
                </div>
                <span className="text-sm leading-relaxed text-hms-textSub sm:text-base">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 4 — Guest Booking Portal (BookMyRoom.io)                         */
/* -------------------------------------------------------------------------- */

function GuestPortalSection({ c }: { c: HomeContent }) {
  return (
    <section id="for-hotels-guest-portal" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 70% 50%, rgba(13,158,153,0.05) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p id="for-hotels-portal-badge" className="mb-3 text-xs font-semibold uppercase tracking-widest text-hms-teal">
            {c.portal.badge}
          </p>
          <h2
            id="for-hotels-portal-title"
            className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-teal via-teal-400 to-emerald-400 bg-clip-text text-transparent')}
          >
            {c.portal.title}
          </h2>
          <p id="for-hotels-portal-subtitle" className="mt-4 text-base text-hms-textSub sm:text-lg">
            {c.portal.subtitle}{' '}
            <a id="hotels-forhotels-bookmyroom-link" href="https://bookmyroom.io" target="_blank" rel="noopener noreferrer" className="font-semibold text-hms-teal hover:underline">
              BookMyRoom.io
            </a>
            {' '}{c.portal.subtitleLink}
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.portal.items.map((feat, idx) => {
            const Icon = PORTAL_ICONS[idx] ?? Search;
            return (
              <div
                key={idx}
                id={`for-hotels-portal-feature-${idx}`}
                className={cn('rounded-xl border border-hms-border bg-hms-card p-6', 'transition-all duration-300 hover:border-hms-teal/20 hover:-translate-y-1')}
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-hms-teal/10">
                  <Icon className="h-5 w-5 text-hms-teal" />
                </div>
                <h3 className="mb-1.5 text-sm font-bold text-hms-text">{feat.title}</h3>
                <p className="text-[13px] leading-relaxed text-hms-textSub">{feat.desc}</p>
              </div>
            );
          })}
        </div>
        <div id="for-hotels-portal-flow" className="mt-12 mx-auto max-w-3xl">
          <div className="rounded-xl border border-hms-border bg-hms-surface p-6 sm:p-8">
            <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-between">
              <div className="text-center sm:text-left">
                <p className="text-sm font-semibold text-hms-text">{c.portal.howItWorks}</p>
                <p className="mt-1 text-[13px] text-hms-textSub">{c.portal.howItWorksDesc}</p>
              </div>
              <a
                id="for-hotels-portal-visit-btn"
                href="https://bookmyroom.io"
                target="_blank"
                rel="noopener noreferrer"
                className={cn('inline-flex items-center gap-2 rounded-lg border border-hms-teal/30 px-6 py-2.5', 'text-sm font-semibold text-hms-teal whitespace-nowrap', 'transition-all duration-200 hover:bg-hms-teal/5 hover:border-hms-teal/50')}
              >
                {c.portal.visitBtn}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 5 — Final CTA                                                    */
/* -------------------------------------------------------------------------- */

function FinalCTASection({ c }: { c: HomeContent }) {
  return (
    <section id="for-hotels-final-cta" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2
          id="for-hotels-final-cta-title"
          className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}
        >
          {c.cta.title}
        </h2>
        <p id="for-hotels-final-cta-subtitle" className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">
          {c.cta.subtitle}
        </p>
        <div id="for-hotels-final-cta-actions" className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id="for-hotels-final-cta-trial-btn"
            href="/register-hotel"
            className={cn('inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-bg', 'shadow-lg shadow-hms-gold/20 transition-all duration-200', 'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]', 'w-full sm:w-auto justify-center')}
          >
            {c.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id="for-hotels-final-cta-contact-btn"
            href="/contact"
            className={cn('inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-text', 'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold', 'w-full sm:w-auto')}
          >
            {c.cta.contactBtn}
          </LocaleLink>
        </div>
        <p id="for-hotels-final-cta-disclaimer" className="mt-5 text-xs text-hms-textDim">
          {c.cta.disclaimer}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  ForHotelsPage — Main Export                                               */
/* -------------------------------------------------------------------------- */

export function ForHotelsPage() {
  const c = usePageContent(HOME_CONTENT);
  return (
    <main id="for-hotels-page">
      <HeroSection c={c} />
      <PlatformFeaturesSection c={c} />
      <WhyChooseSection c={c} />
      <GuestPortalSection c={c} />
      <TrustBadgesSection />
      <PricingSection />
      <Testimonials />
      <FAQSection />
      <FinalCTASection c={c} />
    </main>
  );
}
